/* prettier-ignore-file */
/**
 * Booking wizard — a stepped alternative to the single long-scrolling
 * NewBooking.js page. That page is left untouched and still serves /new-booking.
 *
 * Flow: dates -> products -> transportation -> customer info -> review.
 *
 * ACCESS: public. The route is NOT wrapped in ProtectedRoute — this is the page
 * the website's "Rent Now" buttons link to (book.bonitabikes.com/booking-wizard).
 * The staff menu entry still sits in AuthHeader's hamburger, which only renders
 * for logged-in staff.
 *
 * The final step splits by audience, exactly as NewBooking.js does:
 *   - staff (isAuthenticated) choose sign & pay / bypass waiver / book directly
 *   - customers go straight to the waiver and the normal payment flow
 * Staff-only controls are gated on isAuthenticated inside the steps themselves
 * (rate override in StepProducts, booking modes in StepReview, same-day dates in
 * StepDates), so an anonymous visitor sees only the customer path.
 *
 * Draft state lives in useNewBookingStore, the same persisted store NewBooking.js
 * uses, so a booking half-filled on one page can be finished on the other.
 */

import React, { useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Modal,
  ActivityIndicator,
} from "react-native";
import { useNavigate, useLocation } from "react-router-dom";
import { doc, getDoc, setDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, app } from "../firebase.config";
import AuthHeader from "../components/AuthHeader";
import RentalCalculatorModal from "../components/RentalCalculatorModal";
import StepDates from "../components/wizard/StepDates";
import StepProducts from "../components/wizard/StepProducts";
import StepTransportation from "../components/wizard/StepTransportation";
import StepCustomerInfo, { isValidEmail, isValidPhone } from "../components/wizard/StepCustomerInfo";
import StepReview from "../components/wizard/StepReview";
import WizardSummary from "../components/wizard/WizardSummary";
import {
  useBookings,
  usePendingBookings,
  useProducts,
  useSettings,
  useAuthStore,
  useNewBookingStore,
  useStripeTestMode,
} from "../stores";
import { getTenantId, getStoreId } from "../services/userStoreCache";
import { loadWaiverTemplate, validateStoredWaiver } from "../utils/waiverCheck";
import { computeAvailabilityForRange } from "../utils/rentalAvailability";
import { computeRentalQuote, readPricingSettings, MINIMUM_ORDER_CENTS } from "../utils/rentalPricing";
import { buildLineItems, createWizardBooking } from "../utils/createWizardBooking";
import { formatCentsAsUSD } from "../utils/formatting";

const functions = getFunctions(app);

/** Writable fields on useNewBookingStore (stores.js:629-712). */
const DRAFT_FIELDS = new Set([
  "startDate",
  "endDate",
  "currentMonth",
  "selectedBikes",
  "selectedAddOns",
  "dailyRateOverrideCents",
  "deliveryNeeded",
  "homePickupNeeded",
  "inStorePickup",
  "gatedCommunity",
  "communityName",
  "addressNotes",
  "firstName",
  "lastName",
  "customerEmail",
  "customerPhone",
  "addressInput",
  "addressSelected",
  "addressConfirmed",
  "waiverSigned",
  "waiverData",
]);

const STEPS = [
  { key: "dates", label: "Dates" },
  { key: "products", label: "Items" },
  { key: "transport", label: "Transport" },
  { key: "customer", label: "Details" },
  { key: "review", label: "Review" },
];

const BookingWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authChecked = useAuthStore((state) => state.authChecked);
  const bookings = useBookings((state) => state.bookings);
  const pendingBookings = usePendingBookings((state) => state.pendingBookings);
  const products1 = useProducts((state) => state.products1) || [];
  const products2 = useProducts((state) => state.products2) || [];
  const settings = useSettings((state) => state.settings);
  const useTestStripe = useStripeTestMode((state) => state.useTestStripe);

  const draftStore = useNewBookingStore();
  const cfg = readPricingSettings(settings);

  const [stepIndex, setStepIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [calculatorVisible, setCalculatorVisible] = useState(false);
  const [manualBookingMode, setManualBookingMode] = useState("signPay");
  const [submitting, setSubmitting] = useState(false);
  const [miles, setMiles] = useState(null);
  // Holds expire by age, so availability changes with the clock and not just with data.
  // Re-derive once a minute; without this a freed item would only appear on reload.
  const [holdTick, setHoldTick] = useState(0);

  // Clear a stale draft on entry (last touch older than 5 minutes), mirroring
  // NewBooking.js:244-255. Without this the wizard greets a returning visitor with
  // month-old dates — and, worse, keeps a signed-but-unpaid waiver alive forever
  // on a shared device, arming the skip-waiver path indefinitely.
  useEffect(() => {
    const state = useNewBookingStore.getState();
    const stale =
      state.lastUpdatedAt == null ||
      typeof state.lastUpdatedAt !== "number" ||
      Date.now() - state.lastUpdatedAt > 300000;
    if (stale) state.clearBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = setInterval(() => setHoldTick((t) => t + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // inStorePickup is derived, never chosen: it means "at least one leg happens at
  // the shop". Kept in sync here as well as in the transportation step, so a draft
  // carried over from NewBooking.js (where all three flags can be false) still
  // writes a correct booking even if the user never opens that step.
  useEffect(() => {
    const shouldBeInStore = !(draftStore.deliveryNeeded && draftStore.homePickupNeeded);
    if (draftStore.inStorePickup !== shouldBeInStore) {
      draftStore.updateBookingData({ inStorePickup: shouldBeInStore });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftStore.deliveryNeeded, draftStore.homePickupNeeded, draftStore.inStorePickup]);

  // --- Draft accessors -------------------------------------------------------
  // Memoized so their object identity is stable across renders -- these feed
  // useMemo dependency arrays for the availability scan, which is expensive.
  const startDate = useMemo(
    () => (draftStore.startDate ? new Date(draftStore.startDate) : null),
    [draftStore.startDate]
  );
  const endDate = useMemo(
    () => (draftStore.endDate ? new Date(draftStore.endDate) : null),
    [draftStore.endDate]
  );
  const currentMonth = useMemo(
    () => (draftStore.currentMonth ? new Date(draftStore.currentMonth) : new Date()),
    [draftStore.currentMonth]
  );

  const draft = useMemo(
    () => ({
      startDate,
      endDate,
      selectedBikes: draftStore.selectedBikes || {},
      selectedAddOns: draftStore.selectedAddOns || {},
      dailyRateOverrideCents: draftStore.dailyRateOverrideCents ?? null,
      deliveryNeeded: draftStore.deliveryNeeded,
      homePickupNeeded: draftStore.homePickupNeeded,
      inStorePickup: draftStore.inStorePickup,
      gatedCommunity: draftStore.gatedCommunity,
      communityName: draftStore.communityName || "",
      addressNotes: draftStore.addressNotes || "",
      addressInput: draftStore.addressInput || "",
      addressSelected: draftStore.addressSelected,
      addressConfirmed: draftStore.addressConfirmed,
      firstName: draftStore.firstName || "",
      lastName: draftStore.lastName || "",
      customerEmail: draftStore.customerEmail || "",
      customerPhone: draftStore.customerPhone || "",
      waiverSigned: draftStore.waiverSigned,
      waiverData: draftStore.waiverData,
      adminNotes: "",
      miles,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draftStore, miles]
  );

  const updateDraft = (changes) => {
    // `miles` is derived, not part of the persisted draft, so peel it off
    // without mutating the caller's object.
    const { miles: nextMiles, ...storeChanges } = changes;
    if (Object.prototype.hasOwnProperty.call(changes, "miles")) setMiles(nextMiles);

    // updateBookingData() spreads whatever it is given straight into the store, so
    // a misspelled key writes a dead field and the real one silently never changes
    // (this is how `delivery` vs `deliveryNeeded` slipped through). Fail loudly in dev.
    if (process.env.NODE_ENV !== "production") {
      const unknown = Object.keys(storeChanges).filter((key) => !DRAFT_FIELDS.has(key));
      if (unknown.length > 0) {
        console.error(
          `❌ BookingWizard: unknown draft field(s) [${unknown.join(", ")}] — ` +
            `these will not persist. Valid fields: ${[...DRAFT_FIELDS].join(", ")}`
        );
      }
    }

    // A stored waiver belongs to the person named on it. The moment the customer's
    // identity changes, invalidate it so a fresh signature is collected — otherwise
    // the next customer on a shared register books under the previous customer's
    // waiver via the skip-waiver path in goToWaiver.
    const identityChanged = ["firstName", "lastName", "customerEmail", "customerPhone"].some(
      (key) =>
        Object.prototype.hasOwnProperty.call(storeChanges, key) &&
        storeChanges[key] !== draftStore[key]
    );
    if (identityChanged && (draftStore.waiverSigned || draftStore.waiverData)) {
      storeChanges.waiverSigned = false;
      storeChanges.waiverData = null;
    }

    if (Object.keys(storeChanges).length > 0) draftStore.updateBookingData(storeChanges);
  };

  // `miles` lives in component state while `addressConfirmed` is persisted, so a
  // page reload can leave an address "confirmed" with no distance behind it --
  // which would price transportation at the loading fee alone and let the user
  // through checkout under-charged. Force a re-confirm instead.
  useEffect(() => {
    if (draftStore.addressConfirmed && miles == null) {
      draftStore.updateBookingData({ addressConfirmed: false });
    }
    // Only on mount: afterwards the two are kept in sync by handleValidateAddress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Derived: availability and live quote ----------------------------------
  const availability = useMemo(
    () =>
      computeAvailabilityForRange({
        startDate,
        endDate,
        bookings,
        pendingBookings,
        products1Catalog: products1,
        products2Catalog: products2,
      }),
    // holdTick is a dependency on purpose: holds expire with time, not with data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draftStore.startDate, draftStore.endDate, bookings, pendingBookings, products1, products2, holdTick]
  );

  const lineItems = useMemo(
    () =>
      buildLineItems({
        selectedBikes: draft.selectedBikes,
        selectedAddOns: draft.selectedAddOns,
        products1,
        products2,
        dailyRateOverrideCents: draft.dailyRateOverrideCents,
      }),
    [draft.selectedBikes, draft.selectedAddOns, products1, products2, draft.dailyRateOverrideCents]
  );

  const quote = useMemo(
    () =>
      computeRentalQuote({
        startDate,
        endDate,
        product1: lineItems.product1,
        product2: lineItems.product2,
        products1Catalog: products1,
        products2Catalog: products2,
        settings,
        delivery: draft.deliveryNeeded,
        homePickup: draft.homePickupNeeded,
        miles,
        dailyRateOverrideCents: draft.dailyRateOverrideCents,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draftStore.startDate, draftStore.endDate, lineItems, products1, products2, settings, draft.deliveryNeeded, draft.homePickupNeeded, miles, draft.dailyRateOverrideCents]
  );

  // --- Validation ------------------------------------------------------------
  const hasItems = lineItems.product1.length > 0 || lineItems.product2.length > 0;
  const needsAddress = draft.deliveryNeeded || draft.homePickupNeeded;
  const datesValid =
    !!startDate && !!endDate && startDate.getTime() !== endDate.getTime();

  const stepErrors = useMemo(() => {
    switch (stepIndex) {
      case 0:
        return datesValid ? [] : ["Pick a start and end date on different days"];
      case 1:
        return hasItems ? [] : ["Select at least one item"];
      case 2: {
        // No "pick an option" check: the two legs are radio pairs, so a valid
        // combination is always selected (shop->shop is the default).
        const errors = [];
        if (needsAddress && !draft.addressSelected) errors.push("Select your address from the list");
        if (needsAddress && draft.addressSelected && !draft.addressConfirmed) errors.push('Tap "Confirm address & distance"');
        return errors;
      }
      case 3: {
        const errors = [];
        if (!draft.firstName.trim()) errors.push("First name");
        if (!draft.lastName.trim()) errors.push("Last name");
        if (!isValidEmail(draft.customerEmail)) errors.push("A valid email address");
        if (!isValidPhone(draft.customerPhone)) errors.push("A 10-digit phone number");
        return errors;
      }
      default:
        return [];
    }
  }, [stepIndex, datesValid, hasItems, draft, needsAddress]);

  // Everything that must be true before a booking can actually be created.
  const missingFields = useMemo(() => {
    const errors = [];
    if (!datesValid) errors.push("Rental dates");
    if (!hasItems) errors.push("At least one item");
    if (needsAddress && !draft.addressSelected) errors.push("A delivery address selected from the list");
    if (needsAddress && draft.addressSelected && !draft.addressConfirmed) errors.push("A confirmed delivery address");
    if (!draft.firstName.trim()) errors.push("First name");
    if (!draft.lastName.trim()) errors.push("Last name");
    if (!isValidEmail(draft.customerEmail)) errors.push("A valid email address");
    if (!isValidPhone(draft.customerPhone)) errors.push("A 10-digit phone number");
    if (quote.finalTotalCents <= MINIMUM_ORDER_CENTS) {
      errors.push(`A total above ${formatCentsAsUSD(MINIMUM_ORDER_CENTS)}`);
    }
    return errors;
  }, [datesValid, hasItems, draft, needsAddress, quote.finalTotalCents]);

  // Nothing to clear on a blank form, so the button stays out of the way until the
  // customer has actually put something in.
  const hasAnyDraftData =
    !!draftStore.startDate ||
    !!draftStore.endDate ||
    Object.keys(draftStore.selectedBikes || {}).length > 0 ||
    Object.keys(draftStore.selectedAddOns || {}).length > 0 ||
    !!draftStore.firstName ||
    !!draftStore.lastName ||
    !!draftStore.customerEmail ||
    !!draftStore.customerPhone ||
    !!draftStore.addressInput;

  const handleClearBooking = () => {
    if (
      !window.confirm(
        "Clear this booking?\n\nDates, items, transport and customer details will all be reset."
      )
    ) {
      return;
    }
    // clearBooking() resets the persisted draft. miles and the step index live in
    // component state, so they have to be reset alongside it or the form would look
    // empty while still carrying a distance and sitting on a later step.
    draftStore.clearBooking();
    setMiles(null);
    setStepIndex(0);
    setManualBookingMode("signPay");
  };

  // --- Step handlers ---------------------------------------------------------
  const handleSelectDate = (date) => {
    if (!startDate) {
      updateDraft({ startDate: date.toISOString(), endDate: null });
      return;
    }
    if (endDate) {
      // A full range is already selected. Tapping the existing start or end
      // clears the range; tapping any other date reshapes the range around
      // it, moving whichever boundary is nearer instead of starting over.
      if (date.getTime() === startDate.getTime() || date.getTime() === endDate.getTime()) {
        updateDraft({ startDate: null, endDate: null });
        return;
      }
      if (date < startDate) {
        updateDraft({ startDate: date.toISOString() });
        return;
      }
      if (date > endDate) {
        updateDraft({ endDate: date.toISOString() });
        return;
      }
      const distToStart = date - startDate;
      const distToEnd = endDate - date;
      if (distToStart <= distToEnd) {
        updateDraft({ startDate: date.toISOString() });
      } else {
        updateDraft({ endDate: date.toISOString() });
      }
      return;
    }
    if (date < startDate) {
      updateDraft({ startDate: date.toISOString(), endDate: startDate.toISOString() });
      return;
    }
    if (date.getTime() === startDate.getTime()) {
      updateDraft({ startDate: null, endDate: null });
      return;
    }
    updateDraft({ endDate: date.toISOString() });
  };

  const handleBikeChange = (product, nextQuantity) => {
    const info = availability.product1?.[product.id];
    const cap = info?.hasInventory ? info.available : Infinity;
    const quantity = Math.max(0, Math.min(nextQuantity, cap));
    const selectedBikes = { ...draft.selectedBikes };
    if (quantity === 0) delete selectedBikes[product.name];
    else selectedBikes[product.name] = quantity;
    updateDraft({ selectedBikes });
  };

  const handleAddOnChange = (product, nextQuantity) => {
    const option = product.option || "per-booking";
    const info = availability.product2?.[product.id];
    const cap = info?.hasInventory ? info.available : Infinity;
    const quantity = Math.max(0, Math.min(nextQuantity, cap));
    const selectedAddOns = { ...draft.selectedAddOns };
    if (quantity === 0) delete selectedAddOns[product.id];
    else if (option === "per-bike") selectedAddOns[product.id] = true;
    else selectedAddOns[product.id] = { quantity };
    updateDraft({ selectedAddOns });
  };

  // --- Submission ------------------------------------------------------------
  const goToWaiver = async () => {
    const template = await loadWaiverTemplate();
    // A waiver names a person, so an on-file signature is only reusable when its
    // typed name matches the customer on THIS booking. Without the check, the next
    // customer on a shared register inherits the previous customer's waiver: the
    // cash flow clears the draft but Waiver.js used to write the signature back,
    // and validateStoredWaiver only looks at whether the fields are filled in.
    const normalizeName = (value) => String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
    const storedWaiverName = normalizeName(draft.waiverData?.typedName);
    const waiverNameMatches =
      storedWaiverName !== "" &&
      storedWaiverName === normalizeName(`${draft.firstName} ${draft.lastName}`);
    const canSkipWaiver =
      !!(draft.waiverSigned && draft.waiverData && waiverNameMatches && template &&
        validateStoredWaiver(draft.waiverData, template));

    // When the waiver page is skipped, nothing downstream persists the booking —
    // the customer goes straight to Stripe, and the webhook can only confirm a
    // booking that exists in pendingBookings. So the skip path must save NOW
    // (buildWizardBookingData already embeds the stored waiver). The normal path
    // stays unsaved here because Waiver.js creates the document once the waiver
    // is actually signed.
    const result = await createWizardBooking({
      draft,
      products1,
      products2,
      settings,
      useTestStripe,
      saveToFirestore: canSkipWaiver,
    });

    if (canSkipWaiver) {
      navigate("/stripe-checkout", {
        state: {
          paymentUrl: result.paymentUrl,
          bookingId: result.bookingId,
          referrer: "/booking-wizard",
        },
      });
      return;
    }

    // Timestamps must be serializable to survive router state.
    const serializable = {
      ...result.bookingData,
      createdAt: result.bookingData.createdAt.toDate().toISOString(),
      startDate: result.bookingData.startDate.toDate().toISOString(),
      endDate: result.bookingData.endDate.toDate().toISOString(),
      createdBy: {
        ...result.bookingData.createdBy,
        timestamp: result.bookingData.createdBy.timestamp.toDate().toISOString(),
      },
      waiverData: [],
    };

    navigate("/waiver", {
      state: {
        customerName: `${draft.firstName} ${draft.lastName}`.trim(),
        hasBicycles: lineItems.product1.length > 0,
        paymentUrl: result.paymentUrl,
        bookingId: result.bookingId,
        bookingData: serializable,
        pendingBookingsPath: result.pendingBookingsPath,
        // Backing out of Stripe should land back here, not on the old page.
        referrer: "/booking-wizard",
      },
    });
  };

  /**
   * Cash: save the booking to pendingBookings marked as awaiting cash, then send the
   * customer through the waiver. Waiver.js hands off to /cash-payment/:bookingId,
   * where staff take the money at the register and confirm — that confirmation is what
   * promotes it to a real, paid booking. No Stripe link is created.
   */
  const goToCashWaiver = async () => {
    const result = await createWizardBooking({
      draft,
      products1,
      products2,
      settings,
      useTestStripe,
      saveToFirestore: true,
      cashPayment: true,
    });

    draftStore.clearBooking();
    navigate("/waiver", {
      state: {
        customerName: `${draft.firstName} ${draft.lastName}`.trim(),
        hasBicycles: lineItems.product1.length > 0,
        bookingId: result.bookingId,
        pendingBookingsPath: result.pendingBookingsPath,
        cashPayment: true,
      },
    });
  };

  const bookDirectly = async () => {
    const result = await createWizardBooking({
      draft,
      products1,
      products2,
      settings,
      useTestStripe,
      saveToFirestore: true,
    });

    const tenantId = getTenantId();
    const storeId = getStoreId();
    const bookingsPath = tenantId && storeId ? `tenants/${tenantId}/stores/${storeId}/bookings` : "bookings";

    const pendingSnap = await getDoc(doc(db, result.pendingBookingsPath, result.bookingId));
    if (!pendingSnap.exists()) throw new Error("Pending booking not found");

    const finalBookingData = {
      ...pendingSnap.data(),
      status: "confirmed",
      payments: [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    delete finalBookingData.tempCostBreakdown;

    await setDoc(doc(db, bookingsPath, result.bookingId), finalBookingData);
    await deleteDoc(doc(db, result.pendingBookingsPath, result.bookingId));

    try {
      const notify = httpsCallable(functions, "sendManualBookingNotifications");
      await notify({ bookingId: result.bookingId, tenantId, storeId });
    } catch (error) {
      // Notification failure must not block the booking (NewBooking.js:1966).
      console.error("⚠️ Manual booking notifications failed:", error);
    }

    draftStore.clearBooking();
    navigate(`/view-booking/${result.bookingId}`);
  };

  const handleSubmit = async () => {
    if (missingFields.length > 0) return;

    // Customers always take the waiver path; only staff see the mode switches.
    const mode = isAuthenticated ? manualBookingMode : "signPay";

    if (mode === "bypassWaiver") {
      if (!window.confirm("Confirm Bypass Waiver? This will skip the waiver and go directly to payment.")) return;
    }
    if (mode === "direct") {
      if (!window.confirm("Confirm Manual Booking (No Payment)? This will create a confirmed booking without payment.")) return;
    }
    setSubmitting(true);
    try {
      if (mode === "signPay") {
        await goToWaiver();
      } else if (mode === "signPayCash") {
        await goToCashWaiver();
      } else if (mode === "bypassWaiver") {
        const result = await createWizardBooking({
          draft,
          products1,
          products2,
          settings,
          useTestStripe,
          saveToFirestore: true,
        });
        navigate("/stripe-checkout", {
          state: { paymentUrl: result.paymentUrl, bookingId: result.bookingId, referrer: "/booking-wizard" },
        });
      } else {
        await bookDirectly();
      }
    } catch (error) {
      console.error("❌ Booking submission failed:", error);
      alert(`Booking failed: ${error.message || "Unknown error"}`);
    } finally {
      setSubmitting(false);
    }
  };

  // --- Render ----------------------------------------------------------------
  if (!authChecked) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#0E7C6B" />
      </View>
    );
  }

  const step = STEPS[stepIndex];
  const canAdvance = stepErrors.length === 0;

  const renderStep = () => {
    switch (step.key) {
      case "dates":
        return (
          <StepDates
            startDate={startDate}
            endDate={endDate}
            currentMonth={currentMonth}
            onSelectDate={handleSelectDate}
            onMonthChange={(month) => updateDraft({ currentMonth: month.toISOString() })}
            bookings={bookings}
            pendingBookings={pendingBookings}
            holdTick={holdTick}
            products1={products1}
            product1Title={cfg.product1Title}
            availability={availability}
            isAuthenticated={isAuthenticated}
            isDesktop={isDesktop}
          />
        );
      case "products":
        return (
          <StepProducts
            products1={products1}
            products2={products2}
            product1Title={cfg.product1Title}
            product2Title={cfg.product2Title}
            selectedBikes={draft.selectedBikes}
            selectedAddOns={draft.selectedAddOns}
            onBikeChange={handleBikeChange}
            onAddOnChange={handleAddOnChange}
            availability={availability}
            totalBikes={lineItems.totalBikes}
            isAuthenticated={isAuthenticated}
            dailyRateOverrideCents={draft.dailyRateOverrideCents}
            onRateOverrideChange={(cents) => updateDraft({ dailyRateOverrideCents: cents })}
          />
        );
      case "transport":
        return (
          <StepTransportation
            delivery={draft.deliveryNeeded}
            homePickup={draft.homePickupNeeded}
            inStorePickup={draft.inStorePickup}
            addressInput={draft.addressInput}
            addressSelected={draft.addressSelected}
            addressConfirmed={draft.addressConfirmed}
            gatedCommunity={draft.gatedCommunity}
            communityName={draft.communityName}
            addressNotes={draft.addressNotes}
            miles={miles}
            quote={quote}
            settings={settings}
            onChange={updateDraft}
          />
        );
      case "customer":
        return (
          <StepCustomerInfo
            firstName={draft.firstName}
            lastName={draft.lastName}
            customerEmail={draft.customerEmail}
            customerPhone={draft.customerPhone}
            onChange={updateDraft}
          />
        );
      case "review":
        return (
          <StepReview
            isAuthenticated={isAuthenticated}
            quote={quote}
            draft={draft}
            products1={products1}
            products2={products2}
            product1Title={cfg.product1Title}
            product2Title={cfg.product2Title}
            manualBookingMode={manualBookingMode}
            onModeChange={setManualBookingMode}
            onSubmit={handleSubmit}
            submitting={submitting}
            missingFields={missingFields}
            cancellationPolicy={settings?.cancellationPolicy?.policy}
          />
        );
      default:
        return null;
    }
  };

  return (
    <View style={styles.container}>
      <AuthHeader
        title="Book a Rental"
        subtitle={cfg.product1Title}
        isDesktop={isDesktop}
        isAuthenticated={isAuthenticated}
        shopInfo={{
          address: cfg.startingAddress,
          phone: settings?.storePhone?.phone ?? "",
          email: settings?.storeEmail?.email ?? "",
        }}
        storeLogo={settings?.storeLogo?.url ?? ""}
        showHelperBar={false}
      />

      <View style={isDesktop ? styles.desktopWrapper : styles.mobileWrapper}>
        {/* Progress */}
        <View style={styles.stepper}>
          {STEPS.map((s, index) => {
            const done = index < stepIndex;
            const active = index === stepIndex;
            return (
              <TouchableOpacity
                key={s.key}
                style={styles.stepperItem}
                // Only allow jumping back to steps already completed.
                onPress={() => index < stepIndex && setStepIndex(index)}
                disabled={index >= stepIndex}
              >
                <View
                  style={[styles.stepDot, done && styles.stepDotDone, active && styles.stepDotActive]}
                >
                  <Text style={[styles.stepDotText, (done || active) && styles.stepDotTextActive]}>
                    {done ? "✓" : index + 1}
                  </Text>
                </View>
                <Text style={[styles.stepLabel, active && styles.stepLabelActive]}>{s.label}</Text>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Only on the first step: that is where a shopper is deciding whether to
            proceed at all. Past that point the running summary below answers the
            price question, and this row would just cost vertical space on a phone. */}
        {step.key === "dates" && (
          <View style={styles.stepHeader}>
            <TouchableOpacity
              dataSet={{ hover: "lift" }}
              style={styles.calcButton}
              onPress={() => setCalculatorVisible(true)}
            >
              <Text style={styles.calcButtonText}>🧮 Estimate my cost</Text>
            </TouchableOpacity>

            {hasAnyDraftData && (
              <TouchableOpacity
                dataSet={{ hover: "lift" }}
                style={styles.clearButton}
                onPress={handleClearBooking}
              >
                <Text style={styles.clearButtonText}>Clear booking</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        <ScrollView style={styles.content}>
          {renderStep()}

          {stepErrors.length > 0 && stepIndex < STEPS.length - 1 && (
            <View style={styles.stepErrorBox}>
              {stepErrors.map((error) => (
                <Text key={error} style={styles.stepErrorText}>
                  • {error}
                </Text>
              ))}
            </View>
          )}

          {/* Running summary. Skipped on review, which already itemises everything. */}
          {step.key !== "review" && (
            <WizardSummary
              draft={draft}
              quote={quote}
              products2={products2}
              product1Title={cfg.product1Title}
              product2Title={cfg.product2Title}
              useDurationDiscounts={cfg.useDurationDiscounts}
              durationDiscounts={cfg.durationDiscounts}
            />
          )}

          <View style={styles.bottomSpacer} />
        </ScrollView>

        {/* Navigation */}
        <View style={styles.navBar}>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.navButton, styles.backButton, stepIndex === 0 && styles.navButtonHidden]}
            onPress={() => setStepIndex((i) => Math.max(0, i - 1))}
            disabled={stepIndex === 0}
          >
            <Text style={styles.backButtonText}>Back</Text>
          </TouchableOpacity>

          {stepIndex < STEPS.length - 1 ? (
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.navButton, styles.nextButton, !canAdvance && styles.navButtonDisabled]}
              onPress={() => setStepIndex((i) => Math.min(STEPS.length - 1, i + 1))}
              disabled={!canAdvance}
            >
              <Text style={styles.nextButtonText}>Next</Text>
            </TouchableOpacity>
          ) : (
            <View style={styles.navButton} />
          )}
        </View>
      </View>

      {/* No draft data passed in: the estimator is a standalone what-if tool. */}
      <RentalCalculatorModal
        visible={calculatorVisible}
        onClose={() => setCalculatorVisible(false)}
      />

      <Modal visible={submitting} transparent animationType="fade">
        <View style={styles.submittingOverlay}>
          <ActivityIndicator size="large" color="#FFFFFF" />
          <Text style={styles.submittingText}>Creating your booking…</Text>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#F6F8F7", minHeight: "100vh", display: "flex", flexDirection: "column" },
  loadingContainer: { flex: 1, alignItems: "center", justifyContent: "center", minHeight: "100vh" },
  desktopWrapper: { width: "100%", maxWidth: 800, flex: 1, alignSelf: "center", flexDirection: "column" },
  mobileWrapper: { flex: 1, flexDirection: "column" },

  stepper: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 10,
    backgroundColor: "#FFFFFF",
  },
  stepperItem: { alignItems: "center", flex: 1, cursor: "pointer" },
  stepDot: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#DDE3E2",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 4,
  },
  stepDotDone: { backgroundColor: "#1F8A5B" },
  stepDotActive: { backgroundColor: "#0E7C6B" },
  stepDotText: { fontSize: 12, fontWeight: "bold", color: "#8A9794" },
  stepDotTextActive: { color: "#FFFFFF" },
  stepLabel: { fontSize: 10, color: "#8A9794" },
  stepLabelActive: { color: "#0E7C6B", fontWeight: "600" },

  stepHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 8,
  },

  content: { flex: 1, paddingHorizontal: 16 },
  bottomSpacer: { height: 16 },

  stepErrorBox: {
    backgroundColor: "#FDF3E3",
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: "#B4761B",
    marginBottom: 12,
  },
  stepErrorText: { fontSize: 12, color: "#2E3A38", paddingVertical: 1 },

  navBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    backgroundColor: "#FFFFFF",
    borderTopWidth: 1,
    borderTopColor: "#DDE3E2",
  },
  navButton: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: "center",
    cursor: "pointer",
    marginHorizontal: 5,
    minHeight: 48, // comfortable phone touch target
    justifyContent: "center",
  },
  navButtonHidden: { opacity: 0 },
  navButtonDisabled: { backgroundColor: "#DDE3E2" },
  backButton: { backgroundColor: "#EDF1F0" },
  backButtonText: { color: "#2E3A38", fontSize: 15, fontWeight: "600" },
  nextButton: { backgroundColor: "#0E7C6B" },
  nextButtonText: { color: "#FFFFFF", fontSize: 15, fontWeight: "600" },

  // Text button, not an icon: it needs to read as an offer ("find out the price")
  // rather than a tool, since customers see it too.
  calcButton: { paddingVertical: 8, paddingRight: 8, minHeight: 40, justifyContent: "center", cursor: "pointer" },
  calcButtonText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#0E7C6B",
    textDecorationLine: "underline",
  },

  // Destructive, so it reads as danger — but text-only, not a solid button, so it
  // never competes with the step's actual action.
  clearButton: { paddingVertical: 8, paddingLeft: 8, minHeight: 40, justifyContent: "center", cursor: "pointer" },
  clearButtonText: { fontSize: 14, fontWeight: "600", color: "#C4453D" },

  submittingOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.6)",
    alignItems: "center",
    justifyContent: "center",
  },
  submittingText: { color: "#FFFFFF", fontSize: 15, marginTop: 12 },
});

export default BookingWizard;
